import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  IconChevronLeft,
  IconCalendarEvent,
  IconClockHour3,
  IconAnchor,
  IconPlaneArrival,
  IconPlaneDeparture,
  IconX,
  IconCircleFilled,
} from '@tabler/icons-react';
import { FC } from 'react';
import {
  formatDate,
  formatDateRange,
  formatTo12HourTimeOnly,
} from '../../../common/utilities/dateFormatter';
import { AvailabilityDetail } from '../../../gql/graphql';
import useGetTripBenefits from '../../../hooks/useGetTripBenefit';
import useGetTripTransfer from '../../../hooks/useGetTripTransfer';
import useDiveSites from '../../../hooks/useDiveSites';

interface BoatItineraryDialogProps {
  showItineraryModal: boolean;
  setShowItineraryModal: (value: boolean) => void;
  selectedItinerary: AvailabilityDetail | null;
  boatName: string;
  tripId: number;
  isDesktopVersion: boolean;
  setSelectedDetail: (value: AvailabilityDetail | null) => void;
  selectedDetail: AvailabilityDetail | null;
  scrollToBoatRoomComponent: () => void;
}

const BoatItineraryDialog: FC<BoatItineraryDialogProps> = ({
  showItineraryModal,
  setShowItineraryModal,
  selectedItinerary,
  boatName,
  tripId,
  isDesktopVersion,
  selectedDetail,
  scrollToBoatRoomComponent,
  setSelectedDetail,
}) => {
  const { tripBenefits } = useGetTripBenefits(tripId, showItineraryModal);
  const { tripTransfer } = useGetTripTransfer(tripId, showItineraryModal);
  const { diveSites, isLoading: diveSitesLoading } = useDiveSites(
    tripId,
    showItineraryModal,
  );

  const handleChooseTripAction = () => {
    setShowItineraryModal(false);
    setSelectedDetail(selectedItinerary);
  };

  const handleChooseAvailableRoomAction = () => {
    setShowItineraryModal(false);
    scrollToBoatRoomComponent();
  };

  return (
    <Dialog
      fullScreen={!isDesktopVersion}
      open={showItineraryModal}
      onClose={isDesktopVersion ? () => setShowItineraryModal(false) : () => {}}
      slotProps={
        isDesktopVersion
          ? {
              paper: {
                sx: {
                  m: 2,
                  width: '100%',
                  maxWidth: 'min(800px, calc(100vw - 32px))',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                },
              },
            }
          : undefined
      }
    >
      {isDesktopVersion ? null : (
        <DialogTitle>
          <div className="flex items-end justify-start w-full cursor-pointer">
            <IconChevronLeft
              onClick={() => setShowItineraryModal(false)}
              size={24}
            />
          </div>
        </DialogTitle>
      )}
      <DialogContent
        sx={
          isDesktopVersion
            ? {
                flex: '1 1 auto',
                overflowY: 'auto',
                minHeight: 0,
              }
            : {}
        }
      >
        <div className="flex flex-col items-start gap-12 self-stretch">
          <div className="flex flex-col items-start gap-2 self-stretch">
            <div className="text-2xl font-semibold flex items-center justify-between w-full text-neutral-800 md:text-3xl">
              {selectedItinerary?.routeName}
              <button
                className="hidden md:flex"
                onClick={() => setShowItineraryModal(false)}
              >
                <IconX size={20} />
              </button>
            </div>
            <div className="md:flex">
              <div className="text-neutral-500">
                {formatDateRange(
                  selectedItinerary?.embarkationDate ?? '',
                  selectedItinerary?.disEmbarkationDate ?? '',
                  true,
                )}
                <span className="text-neutral-100 px-3">|</span>
                {selectedItinerary?.numberOfDive} dives
              </div>
              <span className="hidden md:block text-neutral-100 px-3">|</span>
              <div className="text-neutral-500">{boatName}</div>
            </div>
          </div>
          {/* <div className="flex flex-col items-start gap-4 self-stretch">
              <div>Requirement</div>
              <div className="flex flex-col items-start gap-4 self-stretch">
                <div className="flex items-start gap-2">
                  <IconCertificate />
                  <div className="flex flex-col justify-center items-start gap-2">
                    <span>Advance Open Water</span>
                    <span>with 5 dives or more</span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <IconClipboardHeart />
                  <span>Dive and travel insurance</span>
                </div>
              </div>
            </div> */}
          <div className="flex flex-col gap-12">
            <div className="flex flex-col lg:grid lg:grid-cols-2 gap-6">
              <div className="flex flex-col items-start gap-4">
                <span className="font-semibold text-neutral-800">
                  Departure
                </span>
                <div className="flex items-center gap-2">
                  <IconCalendarEvent />
                  <span>
                    {formatDate(selectedItinerary?.embarkationDate ?? '')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IconClockHour3 />
                  <span>
                    {formatTo12HourTimeOnly(
                      tripTransfer?.tripTransfer.embarkationTime ?? '',
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IconAnchor />
                  <span>{tripTransfer?.tripTransfer.departurePierName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <IconPlaneArrival />
                  <span>
                    {tripTransfer?.tripTransfer.departureAirportName} (
                    {tripTransfer?.tripTransfer.departureAirportCode})
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-start gap-4">
                <span className="font-semibold text-neutral-800">Return</span>
                <div className="flex items-center gap-2">
                  <IconCalendarEvent />
                  <span>
                    {formatDate(selectedItinerary?.disEmbarkationDate ?? '')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IconClockHour3 />
                  <span>
                    {formatTo12HourTimeOnly(
                      tripTransfer?.tripTransfer.disembarkationTime ?? '',
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <IconAnchor />
                  <span>{tripTransfer?.tripTransfer.returnPierName}</span>
                </div>
                <div className="flex items-center gap-2">
                  <IconPlaneDeparture />
                  <span>
                    {tripTransfer?.tripTransfer.returnAirportName} (
                    {tripTransfer?.tripTransfer.returnAirportCode})
                  </span>
                </div>
              </div>
            </div>

            {!diveSitesLoading && diveSites.length > 0 && (
              <div className="flex flex-col items-start gap-4 self-stretch">
                <span className="font-semibold text-neutral-800">
                  Highlight dive sites
                </span>
                <span className="text-sm text-neutral-500">
                  The following locations are potential dive sites; however,
                  their availability cannot be guaranteed due to unpredictable
                  weather conditions.
                </span>
                <div className="grid grid-cols-1 gap-4 self-stretch sm:grid-cols-2">
                  {diveSites.map(site => (
                    <div
                      className="flex items-center gap-2"
                      key={site.diveSiteId}
                    >
                      <IconCircleFilled
                        size={24}
                        className="text-neutral-800"
                      />
                      <span className="text-neutral-800">{site.siteName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2">
              <div className="flex flex-col items-start gap-4 self-stretch">
                <span className="font-semibold text-neutral-800">
                  Trip included
                </span>
                <ul className="list-none flex flex-col gap-2 self-stretch p-0 m-0">
                  {tripBenefits?.tripBenefits
                    .filter(x => x.isInclusion)
                    .map(benefit => (
                      <li key={benefit.benefitId}>{benefit.benefitName}</li>
                    ))}
                </ul>
              </div>
              <div className="flex flex-col items-start gap-4 self-stretch">
                <span className="font-semibold text-neutral-800">
                  Trip excluded
                </span>
                <ul className="list-none flex flex-col gap-2 self-stretch p-0 m-0">
                  {tripBenefits?.tripBenefits
                    .filter(x => !x.isInclusion)
                    .map(benefit => (
                      <li key={benefit.benefitId}>{benefit.benefitName}</li>
                    ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-col items-start gap-4 self-stretch">
              <span>Cancellation policy</span>
              <span>
                This booking is non-refundable. Once confirmed, payments are
                non-refundable under any circumstances. Please review your plans
                carefully before booking. For any inquiries, contact our support
                team.
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end px-6 py-4 gap-4 w-full box-border">
          <button
            type="button"
            className="flex h-11 py-3 px-6 justify-center items-center gap-2 w-full sm:w-auto rounded-lg border border-neutral-800 font-semibold text-neutral-800"
            onClick={() => setShowItineraryModal(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="flex h-11 py-3 px-6 justify-center items-center gap-2 w-full sm:w-auto rounded-lg bg-neutral-800 font-semibold text-white"
            onClick={
              selectedDetail
                ? () => handleChooseAvailableRoomAction()
                : () => handleChooseTripAction()
            }
          >
            {selectedDetail ? 'Choose available rooms' : 'Choose this trip'}
          </button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default BoatItineraryDialog;
